/**
 * StyleManager — injects base CSS and handles custom CSS loading
 */

import { getAllCss } from '../styles/index.js';

const BASE_STYLE_ID = 'grb-base-styles';
const CUSTOM_STYLE_ID = 'grb-custom-styles';
const CUSTOM_CSS_TIMEOUT = 5000;

export default class StyleManager {
  constructor() {
    this._baseStyleEl = null;
    this._customStyleEl = null;
  }

  /**
   * Inject base widget styles into <head>
   */
  injectBaseStyles() {
    if (document.getElementById(BASE_STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = BASE_STYLE_ID;
    style.textContent = getAllCss();
    document.head.appendChild(style);
    this._baseStyleEl = style;
  }

  /**
   * Apply CSS custom property overrides from widget-params
   * @param {Object} params - Widget params (primary_color, border_radius, etc.)
   * @param {HTMLElement} rootEl - Widget root element to set vars on
   */
  applyWidgetParams(params, rootEl) {
    if (!params || !rootEl) return;

    const varMap = {
      primary_color: '--grb-color-primary',
      border_radius: '--grb-border-radius',
      font_family: '--grb-font-family',
      grid_columns_desktop: '--grb-grid-columns-desktop',
      grid_columns_mobile: '--grb-grid-columns-mobile',
    };

    for (const [param, cssVar] of Object.entries(varMap)) {
      if (params[param] !== undefined && params[param] !== null) {
        rootEl.style.setProperty(cssVar, String(params[param]));
      }
    }
  }

  /**
   * Load custom CSS from URL with timeout
   * @param {string} url - CSS URL
   * @returns {Promise<void>} Resolves when loaded or timed out
   */
  loadCustomCss(url) {
    return new Promise((resolve) => {
      if (!url) {
        resolve();
        return;
      }

      const link = document.createElement('link');
      link.id = CUSTOM_STYLE_ID;
      link.rel = 'stylesheet';
      link.href = url;

      const cleanup = () => {
        clearTimeout(timer);
        resolve();
      };

      link.onload = cleanup;
      link.onerror = () => {
        console.warn('[GarderoboSearch] Failed to load custom CSS:', url);
        cleanup();
      };

      const timer = setTimeout(() => {
        console.warn('[GarderoboSearch] Custom CSS load timeout, proceeding with defaults');
        resolve();
      }, CUSTOM_CSS_TIMEOUT);

      document.head.appendChild(link);
      this._customStyleEl = link;
    });
  }

  /**
   * Remove all injected styles (on destroy)
   */
  destroy() {
    if (this._baseStyleEl && this._baseStyleEl.parentNode) {
      this._baseStyleEl.parentNode.removeChild(this._baseStyleEl);
      this._baseStyleEl = null;
    }
    if (this._customStyleEl && this._customStyleEl.parentNode) {
      this._customStyleEl.parentNode.removeChild(this._customStyleEl);
      this._customStyleEl = null;
    }
  }
}
